import React from "react";
import { SectionRule } from "components/common/SectionRule";
import { Tooltip } from "@/components/common/Tooltip";
import { plural } from "components/common/ImpactRow";
import { formatShortDate } from "@/lib/dates";
import { formatBytes, formatMoneyCents } from "@/lib/format";
import type { OrgUsage } from "@/lib/api/organizations";
import { UsageRow } from "components/main/organizations/UsageRow";
import { McpUsageStats } from "components/main/organizations/McpUsageBreakdown";
import { useRefreshCooldown } from "components/hooks/useRefreshCooldown";

const countOf = (value: number) => value.toLocaleString();

export type UsageMeter =
  "storage" | "egress" | "mcp_calls" | "agent_trial" | "searches";

export const UsageSection: React.FC<{
  orgId: string;
  usage: OrgUsage | null;
  /// Refetches every meter, and the MCP stats only while they are open.
  onRefresh: () => unknown;
  /// A control at the end of a meter's row (the platform admin's levers).
  actions?: Partial<Record<UsageMeter, React.ReactNode>>;
  /// The per-day MCP stats read a member-only endpoint.
  showMcpStats?: boolean;
}> = ({ orgId, usage, onRefresh, actions = {}, showMcpStats = true }) => {
  const { refresh, refreshing, cooling } = useRefreshCooldown(onRefresh);
  const resetCountdown = (() => {
    if (!usage) return null;
    const on = formatShortDate(usage.egress.resets_at * 1000);
    const left = Math.ceil(
      (usage.egress.resets_at - Date.now() / 1000) / 86_400,
    );
    return {
      label:
        left <= 0
          ? "Resets today"
          : `Resets ${on} · ${left} ${plural(left, "day")}`,
      on,
    };
  })();

  return (
    <section className="flex flex-col">
      <SectionRule
        label="Usage"
        count={
          resetCountdown && (
            <Tooltip title="Egress and tool calls start over each billing month. Storage is a running total.">
              <span className="cursor-help" data-testid="usage-resets">
                {resetCountdown.label}
              </span>
            </Tooltip>
          )
        }
        action={{
          label: "Refresh",
          onClick: () => void refresh(),
          disabled: cooling,
          loading: refreshing,
          testId: "refresh-usage",
        }}
      />
      {usage && (
        <>
          <UsageRow
            label="Storage"
            info="Data held by this organization's tables. A table imported from elsewhere counts against its owner, not you."
            meter={usage.storage}
            format={formatBytes}
            spentNote="Storage is full. Uploads and edits are refused until space is freed."
            action={actions.storage}
          />
          <UsageRow
            label="Egress"
            info="Data leaving Mycorr: exports, downloads, the API and AI tool results. Reading inside the app is free."
            meter={usage.egress}
            format={formatBytes}
            spentNote={`This month's egress allowance is spent. Exports and API reads are refused until ${formatShortDate(usage.egress.resets_at * 1000)}.`}
            action={actions.egress}
          />
          <UsageRow
            label="MCP tool calls"
            info="Tool calls an AI assistant made against this organization's data. What those calls return also counts as egress."
            meter={usage.mcp_calls}
            format={countOf}
            spentNote={`This month's tool calls are used up. They reset ${formatShortDate(usage.mcp_calls.resets_at * 1000)}.`}
            action={actions.mcp_calls}
          />
          {showMcpStats && <McpUsageStats orgId={orgId} />}
          {usage.agent_trial && (
            <UsageRow
              label="AI trial"
              info="What the design and research apps spent on Mycorr's own model. Given once per account; after it, apps run on your own provider key, which that provider bills."
              meter={usage.agent_trial}
              format={(cents) => formatMoneyCents(cents, "USD")}
              spentNote="The trial is used up. Add your own provider key to keep using the apps."
              action={actions.agent_trial}
            />
          )}
          {(usage.searches.limit > 0 || actions.searches) && (
            <UsageRow
              label="Web lookups"
              info="Web lookups the research apps made on Mycorr, whichever key ran the model. Given once per account."
              meter={usage.searches}
              format={countOf}
              spentNote="The web lookups are used up. Research runs carry on without them."
              action={actions.searches}
            />
          )}
        </>
      )}
    </section>
  );
};
